// OnboardingHome.js — with memoization and clean 18-step flow

import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import {
  ChatBubbleLeftRightIcon,
  CheckIcon,
  PlayCircleIcon,
  LockClosedIcon,
  DocumentTextIcon,
} from '@heroicons/react/20/solid';
import {
  ArrowLongRightIcon,
  DocumentMagnifyingGlassIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/24/solid';
import { BuildingOffice2Icon, EnvelopeIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { useNavigate, useParams } from 'react-router-dom';
import StaffNav from './managernav';
import { AuthContext } from '../AuthContext';
import { connectGoogleCalendar } from '../utils/googleSync';
import AIAssistant from './aiassistant';
import DPAAgreement from './DPAAgreement';
import BookShifts from './bookshifts';
import HolidayApprove from './holidayapprove';

import RoleCalibration from './rolecalibration';
import Policies from './policies';
import StaffCosts from './staffcosts';
import CompanyInformation from './companyinfomation';
import Departments from './departments';
import NewsHome from './newshome';
import RoleCalibrationHelp from '../docs/RoleCalibrationHelp';
import PoliciesHelp from '../docs/PoliciesHelp';
import ProcessCreatorHelp from '../docs/ProcessCreatorHelp';
import MembersHelp from '../docs/MembersHelp';
import StaffCostsHelp from '../docs/StaffCostsHelp';
import BookShiftsHelp from '../docs/BookShiftsHelp';
import TaskCalendarHelp from '../docs/TaskCalendarHelp';
import HolidayBookHelp from '../docs/HolidayBookHelp';
import AppraisalFormHelp from '../docs/AppraisalFormHelp';
import CustomerCrmHelp from '../docs/CustomerCrmHelp';
import CreateProjectHelp from '../docs/CreateProjectHelp';
import AnalyticsHelp from '../docs/AnalyticsHelp';
import UserManagementHelp from '../docs/UserManagementHelp';

// ─── Shared tokens ──────────────────────────────────────────────────────────
const DOT_BG = {
  backgroundImage: 'radial-gradient(circle, rgba(99,102,241,0.10) 1.5px, transparent 1.5px)',
  backgroundSize: '32px 32px',
};

const ACCENT_BAR = 'bg-gradient-to-r from-blue-500 via-purple-500 to-emerald-400';

// ─── Generic Training Document Placeholder ────────────────────────────────
const DocPlaceholder = ({ stepNumber, stepName }) => (
  <div className="p-6 max-w-4xl mx-auto">
    <h3 className="text-2xl font-bold text-gray-900 mb-4">Training Document: {stepName}</h3>
    <p className="text-gray-600">
      This is a placeholder for the training document associated with step {stepNumber}.
      Replace this component with the actual training content (video, PDF, interactive guide, etc.).
    </p>
    <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
      <p className="text-sm text-blue-700">
        💡 <strong>Tip:</strong> Use this space to provide a walkthrough, video tutorial, checklist, or reference guide for this step.
      </p>
    </div>
  </div>
);

// ─── Step Configuration (19 steps) ──────────────────────────────────────
const stepsConfig = [
  { number: 1, name: 'Data Processing Agreement', description: 'Review and accept data processing terms.', requiresDPA: false },
  { number: 2, name: 'Google Calendar Sync', description: 'Connect your calendar for meetings.', requiresDPA: true },
  { number: 3, name: 'Role Calibration', description: 'Set roles and expectations for your team.', requiresDPA: true },
  { number: 4, name: 'Company Policies', description: 'Define policies and compliance standards.', requiresDPA: true },
  { number: 5, name: 'Company Information', description: 'Enter your company details.', requiresDPA: true },
  { number: 6, name: 'Add Users', description: 'Invite users and assign roles.', route: '/users', requiresDPA: true },
  { number: 7, name: 'Department & Teams', description: 'Organize your team into departments and teams.', requiresDPA: true },
  { number: 8, name: 'Finance Centre', description: 'Manage and track staff costs, expenses, set milestones and project budgets.', route: '/finances', requiresDPA: true },
  { number: 9, name: 'Process & SOPs', description: 'Create and manage your processes.', route: '/process-creator', requiresDPA: true },
  { number: 10, name: 'Book Your Shifts', description: 'Set up shift scheduling and overtime.', requiresDPA: true },
  { number: 11, name: 'Set Tasks', description: 'Create tasks and assign them to your team.', route: '/taskcalendar', requiresDPA: true },
  { number: 12, name: 'Holiday', description: 'Manage holiday requests and approvals.', requiresDPA: true },
  { number: 13, name: 'Staff Reporting', description: 'View reports on staff performance and attendance.', route: '/appraisals', requiresDPA: true },
  { number: 14, name: 'Training Centre', description: 'Access and assign training courses.', route: '/training', requiresDPA: true },
  { number: 15, name: 'Sales / Project CRM', description: 'Manage client relationships, sales, and project delivery.', route: '/customercrm', requiresDPA: true },
  { number: 16, name: 'Create Your First Project', description: 'Start a new project and define its goals.', route: '/project-management', requiresDPA: true },
  { number: 17, name: 'Send your first message', description: 'Communicate with your team via instant messaging.', route: '/chatdashboard', requiresDPA: true },
  { number: 18, name: 'Create your first post', description: 'Share updates and announcements on your team feed.', route: '/posts', requiresDPA: true },
  { number: 19, name: 'Data Layer', description: 'Set up your business data model and financial dashboard.', route: '/data-layer', requiresDPA: true },
];

// ─── Document Component Mapping ────────────────────────────────────────────
const docComponentMap = {
  1: null,
  2: null,
  3: (props) => <RoleCalibrationHelp {...props} />,
  4: (props) => <PoliciesHelp {...props} />,
  5: null,
  6: (props) => <UserManagementHelp {...props} />,
  7: (props) => <MembersHelp {...props} />,
  8: (props) => <StaffCostsHelp {...props} />,
  9: (props) => <ProcessCreatorHelp {...props} />,
  10: (props) => <BookShiftsHelp {...props} />,
  11: (props) => <TaskCalendarHelp {...props} />,
  12: (props) => <HolidayBookHelp {...props} />,
  13: (props) => <AppraisalFormHelp {...props} />,
  14: null,
  15: (props) => <CustomerCrmHelp {...props} />,
  16: (props) => <CreateProjectHelp {...props} />,
  17: (props) => <AnalyticsHelp {...props} />,
  18: null,
  19: null,
};

// ─── GoogleSyncStep ─────────────────────────────────────────────────────────
const GoogleSyncStep = ({ user, connectGoogleCalendar }) => {
  const clientId = '115634504904-muse9m4tnn7e03hbq35od6b3ehjiu0ti.apps.googleusercontent.com';
  const handleCopyId = () => { navigator.clipboard.writeText(clientId); alert('Client ID copied to clipboard!'); };
  return (
    <div className="text-center py-8 max-w-xl mx-auto px-4">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-50 to-purple-50 border border-indigo-100 mb-5 shadow-sm">
        <img className="h-8 w-8" src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" />
      </div>
      <h3 className="text-xl font-bold text-gray-900">Sync Google Calendar</h3>
      <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto leading-relaxed">
        Connect your calendar to enable seamless meeting scheduling, interview management, and automatic
        event creation directly from InsightOS.
      </p>
      <div className="mt-6 rounded-xl bg-amber-50 p-5 border border-amber-200 text-left text-xs text-gray-700 space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-amber-600 text-lg font-bold">⚠️</span>
          <p className="font-semibold text-gray-900">Required Step: Workspace IT Administrator Approval</p>
        </div>
        <p className="text-gray-600">
          Because InsightOS is a secure B2B platform, your organization's Google Workspace IT Admin must trust our Client ID in-house before you can connect your calendar. Please send them these instructions:
        </p>
        <ol className="list-decimal pl-4 space-y-1.5 font-medium text-gray-800">
          <li>Log into the <a href="https://google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline font-semibold">Google Admin Console</a>.</li>
          <li>Navigate to <span className="font-semibold text-gray-950">Security &gt; Access and data control &gt; API controls</span>.</li>
          <li>Click <span className="font-semibold text-gray-950">Manage Third-Party App Access</span>.</li>
          <li>Click <span className="font-semibold text-gray-950">Add app</span> and choose <span className="font-semibold text-gray-950">OAuth App Name or Client ID</span>.</li>
          <li>Search for the Client ID below and set the permission state to <span className="text-emerald-700 font-bold">Trusted</span>.</li>
        </ol>
        <div className="bg-white p-3 rounded-lg border border-amber-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mt-2">
          <div className="w-full">
            <span className="block font-bold text-gray-900 mb-0.5">InsightOS Client ID:</span>
            <code className="block bg-gray-50 p-1.5 rounded font-mono text-[10px] break-all select-all text-gray-800 border border-gray-100">
              {clientId}
            </code>
          </div>
          <button onClick={handleCopyId} className="w-full sm:w-auto bg-amber-100 hover:bg-amber-200 text-amber-900 px-3 py-1.5 rounded font-semibold transition-colors whitespace-nowrap text-center text-[11px]">
            Copy ID
          </button>
        </div>
      </div>
      <div className="mt-8 flex flex-col sm:flex-row justify-center items-center gap-4">
        {user?.googleCalendarPermission ? (
          <div className="rounded-2xl bg-emerald-50 p-4 border border-emerald-200 inline-block w-full sm:w-auto">
            <div className="flex items-center justify-center">
              <span className="text-emerald-500 text-xl font-bold">✓</span>
              <h3 className="ml-3 text-base font-semibold text-emerald-800">Calendar Successfully Connected</h3>
            </div>
          </div>
        ) : (
          <button onClick={connectGoogleCalendar} className="w-full sm:w-auto inline-flex items-center justify-center gap-x-2 rounded-xl bg-white px-6 py-3 text-base font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 hover:shadow-md transition-all">
            <img className="h-5 w-5" src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" />
            Connect Google Calendar
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Utility ────────────────────────────────────────────────────────────────
function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

// ─── Step Badge ─────────────────────────────────────────────────────────────
function StepBadge({ number, status, size = 'md' }) {
  const isLocked = status === 'locked';
  const isComplete = status === 'complete';
  const isCurrent = status === 'current';
  const dims = size === 'lg' ? 'h-12 w-12 text-base' : 'h-9 w-9 text-sm';
  return (
    <span className={classNames('relative flex-shrink-0 flex items-center justify-center rounded-full font-bold', dims,
      isLocked ? 'bg-gray-100 border-2 border-gray-200 text-gray-300' :
      isComplete ? 'bg-gradient-to-br from-emerald-500 to-teal-400 text-white shadow-md shadow-emerald-200' :
      isCurrent ? 'bg-gradient-to-br from-blue-500 to-purple-500 text-white shadow-lg shadow-purple-200 ring-2 ring-white' :
      'bg-white border-2 border-indigo-200 text-indigo-400'
    )}>
      {number}
      {isComplete && (<span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-white flex items-center justify-center shadow border border-emerald-100"><CheckIcon className="h-2.5 w-2.5 text-emerald-600" /></span>)}
      {isLocked && (<span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-white border border-gray-200 flex items-center justify-center shadow-sm"><LockClosedIcon className="h-2 w-2 text-gray-400" /></span>)}
    </span>
  );
}

// ─── TRAINING DOCUMENT VIEWER (for new tab route) ────────────────────────
export const TrainingDocPage = () => {
  const { stepNumber } = useParams();
  const step = stepsConfig.find((s) => s.number === parseInt(stepNumber));
  const DocComponent = docComponentMap[stepNumber];
  if (!step || !DocComponent) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <DocumentTextIcon className="mx-auto h-16 w-16 text-gray-300" />
          <p className="mt-4 text-gray-500">Document not found for this step.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <DocumentTextIcon className="h-6 w-6 text-indigo-600" />
            Training Document — Step {step.number}: {step.name}
          </h2>
          <p className="text-sm text-gray-500">Check the previous tab for the component </p>
          <button
            onClick={() => window.close()}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            ✕
          </button>
        </div>
        <div className="p-6">
          <DocComponent stepNumber={step.number} stepName={step.name} />
        </div>
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────────
export default function OnboardingHome() {
  const [currentStepNumber, setCurrentStepNumber] = useState(1);
  const [steps] = useState(stepsConfig); // never changes
  const [AI, SetAI] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [dpaAccepted, setDpaAccepted] = useState(false);
  const [dpaStatus, setDpaStatus] = useState(null);
  const user = useContext(AuthContext);
  const navigate = useNavigate();

  // ─── Memoized callbacks ──────────────────────────────────────────────
  const sendStepToBackend = useCallback(async (stepNumber) => {
    try {
      await fetch('https://api.insightos.co.uk/api/onboardingstep', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ stepNumber }),
      });
    } catch (err) {
      console.error('Failed to update onboarding step', err);
    }
  }, []);

  // NEW: Mark onboarding as fully complete
  const finishOnboarding = useCallback(async () => {
    try {
      const response = await fetch('https://api.insightos.co.uk/api/onboarding-complete', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        throw new Error('Failed to persist onboarding completion');
      }
      navigate('/dashboard');
    } catch (err) {
      console.error('Failed to finish onboarding', err);
    }
  }, [navigate]);

  const checkDPAStatus = useCallback(async () => {
    try {
      const response = await fetch('https://api.insightos.co.uk/api/dpa-status', {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      });
      const data = await response.json();
      setDpaStatus(data);
      if (data.success && data.accepted === true) {
        setDpaAccepted(true);
        return true;
      } else if (data.accepted === true) {
        setDpaAccepted(true);
        return true;
      } else {
        setDpaAccepted(false);
        return false;
      }
    } catch (error) {
      console.error('Failed to check DPA status', error);
      setDpaStatus(null);
      return false;
    }
  }, []);

  const getOnboardedStep = useCallback(async () => {
    try {
      const response = await fetch('https://api.insightos.co.uk/api/onboardingstep', {
        method: 'GET',
        credentials: 'include',
      });
      const data = await response.json();
      if (data.success) {
        const step = Number(data.step || 1);
        if (step >= stepsConfig.length) {
          navigate('/dashboard', { replace: true });
          return;
        }
        if (step === 1) {
          const dpaStatusResult = await checkDPAStatus();
          if (dpaStatusResult) {
            setDpaAccepted(true);
            setCurrentStepNumber(2);
            await sendStepToBackend(2);
          } else {
            setCurrentStepNumber(1);
          }
        } else {
          setCurrentStepNumber(step);
          if (step > 1) setDpaAccepted(true);
        }
      }
    } catch (error) {
      console.error('Failed to fetch onboarded step', error);
    }
  }, [checkDPAStatus, sendStepToBackend, navigate]);

  useEffect(() => {
    getOnboardedStep();
  }, [getOnboardedStep]);

  const handleGeoTagUpdate = useCallback(async (geoTagValue) => {
    if (!geoTagValue) return;
    try {
      await fetch('https://api.insightos.co.uk/api/company-geotag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ GeoTag: geoTagValue }),
      });
    } catch (error) {
      console.error('Failed to update company geotag', error);
    }
  }, []);

  const handleDPAAccept = useCallback(async (printName) => {
    try {
      await fetch('https://api.insightos.co.uk/api/dpa-accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          accepted: true,
          acceptedAt: new Date().toISOString(),
          printName,
          acceptedBy: user?.email || 'Unknown User',
        }),
      });
      setDpaAccepted(true);
      const nextStepNumber = 2;
      await sendStepToBackend(nextStepNumber);
      setCurrentStepNumber(nextStepNumber);
    } catch (error) {
      console.error('Failed to accept DPA', error);
    }
  }, [user, sendStepToBackend]);

  const openDocInNewTab = useCallback((stepNumber) => {
    const url = `/training-doc/${stepNumber}`;
    window.open(url, '_blank');
  }, []);

  const getStepStatus = useCallback((stepNumber) => {
    const step = steps.find((s) => s.number === stepNumber);
    if (step?.requiresDPA && !dpaAccepted) return 'locked';
    if (stepNumber < currentStepNumber) return 'complete';
    if (stepNumber === currentStepNumber) return 'current';
    return 'upcoming';
  }, [steps, dpaAccepted, currentStepNumber]);

  const handleStepClick = useCallback(async (step) => {
    if (step.requiresDPA && !dpaAccepted) return;
    await sendStepToBackend(step.number);
    setCurrentStepNumber(step.number);
    if (step.route) navigate(step.route);
  }, [dpaAccepted, sendStepToBackend, navigate]);

  // ─── Navigation handlers: do nothing when already on last step ──────
  const handleNext = useCallback(async () => {
    // If we're on the last step, there is no "next" – the Finish button appears instead
    if (currentStepNumber >= steps.length) return;
    const nextStepNumber = currentStepNumber + 1;
    const nextStep = steps.find((s) => s.number === nextStepNumber);
    if (!nextStep) return;
    if (nextStep.requiresDPA && !dpaAccepted) return;

    if (nextStep.route && docComponentMap[nextStepNumber] != null) {
      openDocInNewTab(nextStepNumber);
    }

    await sendStepToBackend(nextStepNumber);
    setCurrentStepNumber(nextStepNumber);
    if (nextStep.route) navigate(nextStep.route);
  }, [currentStepNumber, steps, dpaAccepted, openDocInNewTab, sendStepToBackend, navigate]);

  const handleCompleteRouteStep = useCallback(async () => {
    if (currentStepNumber >= steps.length) return;
    const nextStepNumber = currentStepNumber + 1;
    const nextStep = steps.find((s) => s.number === nextStepNumber);
    if (nextStep?.requiresDPA && !dpaAccepted) return;

    if (nextStep && nextStep.route && docComponentMap[nextStepNumber] != null) {
      openDocInNewTab(nextStepNumber);
    }

    await sendStepToBackend(nextStepNumber);
    setCurrentStepNumber(nextStepNumber);
    if (nextStep && nextStep.route) navigate(nextStep.route);
  }, [currentStepNumber, steps, dpaAccepted, openDocInNewTab, sendStepToBackend, navigate]);

  const handleCloseAIAssistant = useCallback(() => SetAI(false), []);

  // ─── Memoized StepComponents ─────────────────────────────────────────
  const StepComponents = useMemo(() => ({
    1: (props) => <DPAAgreement {...props} dpaStatus={dpaStatus} />,
    2: (props) => <GoogleSyncStep {...props} connectGoogleCalendar={connectGoogleCalendar} />,
    3: (props) => <RoleCalibration {...props} />,
    4: (props) => <Policies {...props} />,
    5: (props) => <CompanyInformation {...props} />,
    6: null,
    7: (props) => <Departments {...props} />,
    8: null,
    9: null,
    10: (props) => <BookShifts {...props} />,
    11: null,
    12: (props) => <HolidayApprove {...props} />,
    13: null,
    14: null,
    15: null,
    16: null,
    17: null,
    18: null,
  }), [dpaStatus]);

  // ─── Render ─────────────────────────────────────────────────────────────
  const CurrentComponent = StepComponents[currentStepNumber];
  const currentStep = steps.find((s) => s.number === currentStepNumber);
  const progressPercent = Math.min(100, Math.round((currentStepNumber / steps.length) * 100));

  return (
    <>
      {AI && <div className="fixed bottom-0 right-0 z-50 p-4"><AIAssistant AiAssistant={AI} onClose={handleCloseAIAssistant} /></div>}
      <StaffNav />
      <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="absolute inset-0 -z-10" style={DOT_BG} aria-hidden="true" />
        <div aria-hidden className="absolute inset-0 pointer-events-none -z-10">
          <div className="absolute -top-32 -left-24 w-[32rem] h-[32rem] bg-blue-200/40 rounded-full blur-[140px]" />
          <div className="absolute top-1/3 -right-24 w-[28rem] h-[28rem] bg-purple-200/40 rounded-full blur-[140px]" />
          <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-emerald-200/30 rounded-full blur-[120px]" />
        </div>

        <div className="flex flex-col lg:flex-row items-start min-h-screen relative">
          {/* ─── SIDEBAR ──────────────────────────────────────────────────── */}
          <aside className={classNames(
            'w-full transition-all duration-300 bg-white/70 backdrop-blur-2xl border-b lg:border-b-0 lg:border-r border-indigo-100 shadow-sm lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto',
            sidebarCollapsed ? 'lg:w-[100px] p-3' : 'lg:w-[400px] xl:w-[440px] p-4 lg:p-6'
          )}>
            <div className={classNames('flex items-center gap-2 mb-4', sidebarCollapsed ? 'flex-col' : 'justify-between')}>
              {!sidebarCollapsed ? (
                <div className="rounded-2xl bg-white/80 border border-indigo-100 px-4 py-3 flex-1 shadow-sm min-w-0">
                  <div className="flex items-center gap-2 text-blue-600 text-xs font-semibold tracking-wide mb-1 truncate">
                    <EnvelopeIcon className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate">{user?.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-900 text-lg font-bold tracking-wider truncate">
                    <BuildingOffice2Icon className="h-5 w-5 text-purple-500 flex-shrink-0" />
                    <span className="truncate">{user?.companyCode}</span>
                  </div>
                </div>
              ) : (
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 border border-indigo-100 flex items-center justify-center">
                  <UserCircleIcon className="h-6 w-6 text-purple-500" />
                </div>
              )}
              <button onClick={() => setSidebarCollapsed((prev) => !prev)} className="inline-flex items-center justify-center rounded-xl bg-white border border-indigo-200 p-2 text-indigo-600 hover:bg-indigo-50 hover:shadow-md transition-all shadow-sm flex-shrink-0">
                {sidebarCollapsed ? <ChevronDoubleRightIcon className="h-5 w-5" /> : <ChevronDoubleLeftIcon className="h-5 w-5" />}
              </button>
            </div>

            {!sidebarCollapsed && (
              <div className="p-2 rounded-md bg-white">
                <a href="https://app.insightos.co.uk/support" className="text-xs text-blue-600 hover:text-purple-700 transition-colors inline-flex items-center mb-5">
                  <DocumentMagnifyingGlassIcon className="h-7 w-7 inline-block mr-1" /> Docs
                </a>
              </div>
            )}

            {!sidebarCollapsed && dpaAccepted && (
              <div className="mb-6">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <PlayCircleIcon className="h-4 w-4 text-purple-500" /> Welcome
                </p>
                <div className="relative p-[3px] rounded-2xl bg-gradient-to-r from-blue-400 via-purple-400 to-emerald-400 shadow-lg">
                  <div className="bg-white rounded-2xl p-1.5">
                    <video src="https://cdn.insightos.co.uk/Welcome%20To%20The%20Future%20Of%20Work%20(2).mp4" controls className="w-full rounded-xl" />
                  </div>
                </div>
              </div>
            )}

            <nav aria-label="Progress">
              <ol className={classNames(
                sidebarCollapsed ? 'flex flex-row gap-3 overflow-x-auto pb-1 lg:flex-col lg:gap-4 lg:overflow-visible lg:pb-0' : 'flex flex-col gap-2.5'
              )}>
                {steps.map((step, idx) => {
                  const status = getStepStatus(step.number);
                  const isLocked = status === 'locked';
                  const hasDoc = docComponentMap[step.number] != null;
                  return (
                    <li key={step.name} className="relative flex items-center flex-shrink-0">
                      <button onClick={() => handleStepClick(step)} disabled={isLocked}
                        className={classNames(
                          'group flex items-center text-left transition-all duration-200 rounded-2xl w-full',
                          sidebarCollapsed ? 'p-1.5 justify-center' : 'p-2.5',
                          isLocked ? 'opacity-70 cursor-not-allowed' :
                          status === 'current' ? 'bg-gradient-to-r from-blue-50 to-purple-50 border border-indigo-100 shadow-sm' : 'hover:bg-indigo-50/60'
                        )}>
                        <StepBadge number={step.number} status={status} />
                        {!sidebarCollapsed && (
                          <span className="ml-3 flex flex-1 min-w-0 items-center justify-between">
                            <span className="flex flex-col min-w-0">
                              <span className={classNames('text-sm font-semibold tracking-wide truncate',
                                status === 'current' ? 'text-gray-900' : status === 'complete' ? 'text-gray-700' : 'text-gray-400'
                              )}>{step.name}</span>
                              <span className="text-xs text-gray-400 truncate">{step.description}</span>
                            </span>
                            {hasDoc && (
                              <button type="button" onClick={(e) => { e.stopPropagation(); openDocInNewTab(step.number); }}
                                className="ml-2 flex-shrink-0 rounded-full p-1.5 text-indigo-500 hover:bg-indigo-100 hover:text-indigo-700 transition-colors"
                                title="Open training document in new tab">
                                <DocumentTextIcon className="h-5 w-5" />
                              </button>
                            )}
                          </span>
                        )}
                      </button>
                      {!sidebarCollapsed && idx < steps.length - 1 && (
                        <span className={classNames('absolute left-[1.55rem] top-[2.85rem] h-3 border-l-2', status === 'complete' ? 'border-emerald-300' : 'border-indigo-100')} />
                      )}
                    </li>
                  );
                })}
              </ol>
            </nav>
            {!sidebarCollapsed && <div className="mt-8 text-xs text-gray-400">&copy; {new Date().getFullYear()} InsightOS. All rights reserved.</div>}
          </aside>

          {/* ─── MAIN CONTENT ────────────────────────────────────────────── */}
          <div className="flex-1 w-full p-4 sm:p-6 lg:p-10 flex flex-col justify-center min-w-0">
            <div className="max-w-4xl mx-auto w-full">
              <div className="relative rounded-3xl overflow-hidden bg-white/80 backdrop-blur-2xl border border-indigo-100 shadow-xl p-5 lg:p-7 mb-6">
                <div className={classNames('absolute inset-x-0 top-0 h-1', ACCENT_BAR)} />
                <div className="flex flex-col sm:flex-row sm:items-start gap-4 lg:gap-5">
                  <StepBadge number={currentStep?.number} status="current" size="lg" />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                      <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 tracking-tight">{currentStep?.name}</h2>
                      <span className="text-xs font-semibold text-purple-700 bg-purple-50 border border-purple-100 rounded-full px-3 py-1 whitespace-nowrap">Step {currentStepNumber} of {steps.length}</span>
                    </div>
                    <p className="text-sm lg:text-base text-gray-500 mb-5">{currentStep?.description}</p>
                    <div className="mb-5">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Progress</span>
                        <span className="text-xs font-semibold text-gray-600">{progressPercent}%</span>
                      </div>
                      <div className="w-full bg-indigo-50 rounded-full h-2 overflow-hidden">
                        <div className={classNames('h-2 rounded-full transition-all duration-500 max-w-full', ACCENT_BAR)} style={{ width: `${progressPercent}%` }} />
                      </div>
                    </div>

                    {/* ─── BUTTON LOGIC ────────────────────────────────── */}
                    {currentStep?.number !== 1 && (
                      <div className="flex flex-col sm:flex-row gap-3">
                        {currentStepNumber < steps.length ? (
                          // Not on last step – show Continue or Complete
                          !currentStep?.route ? (
                            <button
                              onClick={handleNext}
                              disabled={currentStep?.requiresDPA && !dpaAccepted}
                              className={classNames(
                                'flex items-center justify-center px-6 py-3 font-bold rounded-xl shadow-lg transition-all duration-200 active:scale-95 touch-manipulation',
                                (currentStep?.requiresDPA && !dpaAccepted)
                                  ? 'bg-gray-100 text-gray-300 cursor-not-allowed shadow-none'
                                  : 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-emerald-200'
                              )}
                            >
                              <ArrowLongRightIcon className="h-5 w-5 mr-2" /> Continue to Next Step
                            </button>
                          ) : (
                            <button
                              onClick={handleCompleteRouteStep}
                              disabled={currentStep?.requiresDPA && !dpaAccepted}
                              className={classNames(
                                'flex items-center justify-center px-6 py-3 font-bold rounded-xl shadow-lg transition-all duration-200 active:scale-95 touch-manipulation',
                                (currentStep?.requiresDPA && !dpaAccepted)
                                  ? 'bg-gray-100 text-gray-300 cursor-not-allowed shadow-none'
                                  : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-purple-200'
                              )}
                            >
                              <CheckIcon className="h-5 w-5 mr-2" /> Complete Step
                            </button>
                          )
                        ) : (
                          // ─── LAST STEP (18) – FINISH BUTTON ─────────
                          <button
                            onClick={finishOnboarding}
                            className="flex items-center justify-center px-6 py-3 font-bold rounded-xl shadow-lg transition-all duration-200 active:scale-95 touch-manipulation bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white shadow-purple-200"
                          >
                            <CheckIcon className="h-5 w-5 mr-2" /> Finish Onboarding
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="rounded-3xl overflow-hidden bg-white shadow-xl border border-indigo-100">
                <div className={classNames('h-1', ACCENT_BAR)} />
                <div className="p-4 sm:p-5 lg:p-7">
                  {CurrentComponent ? (
                    <CurrentComponent
                      user={user}
                      triggerNext={handleNext}
                      onGeoTagUpdate={handleGeoTagUpdate}
                      onAccept={currentStep?.number === 1 ? handleDPAAccept : undefined}
                    />
                  ) : (
                    <div className="text-center py-10">
                      <span className="text-5xl mb-4 block">🚀</span>
                      <p className="text-lg font-semibold text-gray-800 mb-2">Ready to get started!</p>
                      <p className="text-sm text-gray-500">Complete this step to continue your onboarding journey.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {dpaAccepted && (
          <div className="fixed bottom-6 right-6 z-40">
            <span className="absolute inset-0 rounded-full bg-emerald-300/50 animate-ping" />
            <button onClick={() => SetAI(true)} className="relative inline-flex rounded-full items-center gap-x-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 p-3 lg:p-4 text-white shadow-xl shadow-emerald-200 hover:shadow-2xl hover:scale-105 transition-all duration-200 active:scale-95 touch-manipulation">
              <ChatBubbleLeftRightIcon className="h-7 w-7 lg:h-8 w-8" />
            </button>
          </div>
        )}
      </div>
    </>
  );
}